import {
  BorderRadiusStyles,
  ButtonAndInputStatesConfig,
  ButtonConfigBaseStyles,
  ButtonConfigurations,
  ButtonHeightConfig,
  ButtonIconConfigurations,
  ButtonLoadingIndicatorType,
  ButtonSizeOptions,
  ButtonTypes,
  GlobalThemeConfiguration,
  InputBaseStyles,
  InputLabelConfig,
  InputLabelOptions,
  NewColourPaletteType,
  TooltipStyleConfigurations,
  TypographyStyleConfigurations,
} from '../config.type';
import {
  BorderRadiusKeys,
  ButtonHeightSizeOptions,
  TypographyKeys,
} from '../default-constants/global-styling.enum';
import { GlobalThemes } from '../workflow.type';
import { mapConfirmationDialogStylesToCssString } from './global-theming-confirmation-dialog.utils';

export interface MappedStyles {
  [key: string]: string;
}
export function generateGlobalThemesCssStringsFromThemeConfig(
  globalTheme: GlobalThemeConfiguration[],
  activeThemeIndex: number,
): GlobalThemes[] {
  const globalThemesArray: GlobalThemes[] = [];
  const seenThemeNames = new Set<string>();

  globalTheme.forEach((theme) => {
    // Duplicate theme check
    if (seenThemeNames.has(theme.themeName)) {
      return;
    }
    seenThemeNames.add(theme.themeName);

    const cssVars = generateCssVarsFromGlobalTheme(theme);
    const buttonsCss = mapButtonStylesToCssString(theme.buttons);
    const inputFieldsCss = mapInputFieldStylesToCssString(theme.inputFields);
    const typographyCss = mapTypographyStylesToCssString(theme.typography);
    const textSkeletonCss = mapTextSkeletonStylesToCssString(theme.textSkeleton);
    const radiusCss = mapBorderRadiusStylesToCssString(theme.radius);
    const tooltipCss = mapTooltipStylesToCssString(theme.tooltipConfigurations);
    const confirmationDialogCss = mapConfirmationDialogStylesToCssString(theme);
    const allStyles: MappedStyles = Object.assign(
      {},
      buttonsCss,
      inputFieldsCss,
      typographyCss,
      textSkeletonCss,
      radiusCss,
      tooltipCss,
      confirmationDialogCss,
    );
    let cssString = generateCssStringFromValues(allStyles, cssVars);

    if (theme.generalConfigurations.extraCss) {
      cssString += `\n${theme.generalConfigurations.extraCss}\n`;
    }

    if (theme.generalConfigurations.loaderCss) {
      cssString += `\n${theme.generalConfigurations.loaderCss}\n`;
    }

    globalThemesArray.push({
      globalTheme: theme,
      cssString: cssString,
      activeThemeIndex: activeThemeIndex,
    });
  });
  return globalThemesArray;
}

type StyleMap = { [key: string]: string };

export function mapStylesToCssString(
  stylesMap: { [key in TypographyKeys]?: Record<string, string> } | undefined,
  selectorPrefix: string,
  classPrefix = '',
  includeShortTag?: boolean,
): StyleMap {
  const result: StyleMap = {};

  if (!stylesMap) return result;

  Object.entries(stylesMap).forEach(([key, styles]) => {
    const cssString = Object.entries(styles)
      .map(([property, value]) => `${property}: ${value} !important;`)
      .join(' ');

    // Optional short tag condition (e.g. for h1, h2, etc.)
    if (includeShortTag && key.length < 3) {
      result[`${selectorPrefix} ${key}${classPrefix} `] = cssString;
    }

    result[`${selectorPrefix} ${classPrefix}.${key}`] = cssString;
  });

  return result;
}

export function mapTypographyStylesToCssString(typography: GlobalThemeConfiguration['typography']) {
  return mapStylesToCssString(typography, '#web-app', '', true);
}

export function mapTextSkeletonStylesToCssString(textSkeleton: GlobalThemeConfiguration['textSkeleton']) {
  return mapStylesToCssString(textSkeleton, '#web-app', '.skeleton_item', true);
}

export function generateCssStringFromValues(val: { [x: string]: string }, cssVars: string): string {
  let cssString = cssVars;
  cssString += `
      #web-app button.small, #web-app button.large {
        width: auto;
        height: auto;
      }
      #web-app button.full-width{
        width: 100%;
        height: auto;
      }`;
  if (val) {
    Object.keys(val).forEach((key) => {
      cssString += `\n${key} {${val[key]}}\n`;
    });
  }
  return cssString;
}

export function mapButtonStylesToCssString(buttons: GlobalThemeConfiguration['buttons']): MappedStyles {
  let result: { [key: string]: string } = {};

  Object.keys(buttons)?.forEach((buttonType) => {
    const button = buttons[buttonType as ButtonTypes];
    const baseSelector = `.${buttonType}`;

    // Idle state
    if (button?.idle) {
      result[`#web-app ${baseSelector}`] = generateCSS({
        color: button.idle.textColor?.color,
        'border-color': button.idle['border-color'],
        'background-color': button.idle['background-color'],
        'text-align': (button as ButtonConfigurations)?.baseStyles?.['justify-content'],
        ...getBaseStyles(button as ButtonConfigurations, buttonType as ButtonTypes, buttons),
      });
    }

    // Size variations (small, large, full-width)
    if ((button as ButtonConfigurations)?.size) {
      result = createButtonSizeClassesStyles(button, result, baseSelector);
    }

    // Size variations (small, large, full-width) for secondary button
    if (buttonType === ButtonTypes.SECONDARY && (button as ButtonConfigurations).isLinkPrimaryButtonConfig) {
      result = createButtonSizeClassesStyles(buttons[ButtonTypes.PRIMARY], result, baseSelector);
    }

    // Size variations (small, large, full-width) for secondary button
    if (buttonType === ButtonTypes.TERTIARY && (button as ButtonConfigurations).isLinkPrimaryButtonConfig) {
      result = createButtonSizeClassesStyles(buttons[ButtonTypes.PRIMARY], result, baseSelector);
    }

    // Hover state
    if (button?.hover) {
      result[`#web-app ${baseSelector}:hover`] = generateCSS({
        color: button.hover.textColor?.color,
        'border-color': button.hover['border-color'],
        'background-color': button.hover['background-color'],
      });
      result[`#web-app ${baseSelector}.hover`] = generateCSS({
        color: button.hover.textColor?.color,
        'border-color': button.hover['border-color'],
        'background-color': button.hover['background-color'],
      });
    }

    // Active state
    if (button?.active) {
      result[`#web-app ${baseSelector}:focus`] = generateCSS({
        color: button.active.textColor?.color,
        'border-color': button.active['border-color'],
        'background-color': button.active['background-color'],
      });
      result[`#web-app ${baseSelector}.focus`] = generateCSS({
        color: button.active.textColor?.color,
        'border-color': button.active['border-color'],
        'background-color': button.active['background-color'],
      });
    }

    // icons configuration
    if ((button as ButtonConfigurations)?.iconConfigurations) {
      const { type, iconName, ...rest } = (button as ButtonConfigurations)
        .iconConfigurations as ButtonIconConfigurations;
      result[`#web-app .button-icon`] = generateCSS(rest);
    }
  });

  return result;
}

function getBaseStyles(
  button: ButtonConfigurations,
  buttonType: ButtonTypes,
  buttons: GlobalThemeConfiguration['buttons'],
): ButtonConfigBaseStyles {
  if (
    button.isLinkPrimaryButtonConfig &&
    (buttonType === ButtonTypes.TERTIARY || buttonType === ButtonTypes.SECONDARY)
  ) {
    return {
      ...buttons[ButtonTypes.PRIMARY]?.baseStyles,
    };
  } else {
    return {
      ...button?.baseStyles,
    };
  }
}

export function mapInputFieldStylesToCssString(
  inputFields: GlobalThemeConfiguration['inputFields'],
): MappedStyles {
  const result: MappedStyles = {};

  // Idle state
  if (inputFields.idle) {
    const heightValue = inputFields.inputStyles.height;
    const textAreaHeight =
      parseFloat(heightValue) * 2 + heightValue.replace(parseFloat(heightValue).toString(), '');
    const textAlignment = getTextAlignmentValue(inputFields.inputStyles['justify-content']);
    result['#web-app .inputField'] = generateCSS({
      color: inputFields.idle.textColor?.color + ' !important',
      'border-color': inputFields.idle['border-color'] + ' !important',
      'background-color': inputFields.idle['background-color'] + ' !important',
      'text-align ': textAlignment,
      '-webkit-text-fill-color': 'inherit !important',
      ...inputFields.inputStyles,
    });
    result['#web-app .inputTextArea'] = generateCSS({
      'min-height': textAreaHeight,
    });
    result['#web-app .labelIdle'] = generateCSS({
      color: inputFields.idle.labelColor?.color,
    });

    //Radio Input Button Idle State
    result['#web-app .radioField'] = generateCSS({
      'border-color': inputFields.idle['border-color'],
      'background-color': inputFields.idle['background-color'],
      width: '24px',
      'min-width': '24px',
      height: '24px',
      '-webkit-appearance': 'none',
      appearance: 'none',
      'border-radius': '100%',
      'border-left-width': inputFields.inputStyles['border-left-width'],
      'border-right-width': inputFields.inputStyles['border-right-width'],
      'border-bottom-width': inputFields.inputStyles['border-bottom-width'],
      'border-top-width': inputFields.inputStyles['border-top-width'],
      'justify-content': 'center',
    });

    //Checkbox Input Idle State
    result['#web-app .checkboxInput'] = generateCSS({
      'border-color': inputFields.idle['border-color'],
      'background-color': inputFields.idle['background-color'],
      width: '24px',
      'min-width': '24px',
      height: '24px',
      '-webkit-appearance': 'none',
      appearance: 'none',
      'border-radius': '6px',
      'border-left-width': inputFields.inputStyles['border-left-width'],
      'border-right-width': inputFields.inputStyles['border-right-width'],
      'border-bottom-width': inputFields.inputStyles['border-bottom-width'],
      'border-top-width': inputFields.inputStyles['border-top-width'],
      'justify-content': 'center',
      display: 'flex',
    });
  }

  // Active state
  if (inputFields.active) {
    result['#web-app .inputField:focus'] = generateCSS({
      color: inputFields.active.textColor?.color + ' !important',
      'border-color': inputFields.active['border-color'] + ' !important',
      'background-color': inputFields.active['background-color'] + ' !important',
      '-webkit-text-fill-color': 'inherit !important',
    });
    result['#web-app .inputField.focus'] = generateCSS({
      color: inputFields.active.textColor?.color + ' !important',
      'border-color': inputFields.active['border-color'] + ' !important',
      'background-color': inputFields.active['background-color'] + ' !important',
      '-webkit-text-fill-color': 'inherit !important',
    });
    result['#web-app .labelActive'] = generateCSS({
      color: inputFields.active.labelColor?.color,
    });

    //Radio Input Button Active State
    result['#web-app .radioField:checked::after'] = generateCSS({
      'border-color': inputFields.active['border-color'],
      'background-color': inputFields.active['border-color'],
      'border-radius': '100%',
      content: "''",
      height: '13px',
      width: '13px',
    });
    result['#web-app .radioField:checked'] = generateCSS({
      'border-color': inputFields.active['border-color'],
      'background-color': inputFields.active['background-color'],
    });

    //Checkbox Input Active State
    result['#web-app .checkboxInput:checked::after'] = generateCSS({
      'border-color': inputFields.active['border-color'],
      color: inputFields.active['background-color'],
      content: '"✓"',
      'align-items': 'center',
      display: 'flex',
    });
    result['#web-app .checkboxInput:checked'] = generateCSS({
      'border-color': inputFields.active['border-color'],
      'background-color': inputFields.active['border-color'],
    });
  }

  // Label config
  if (inputFields.labelConfig) {
    const { type, ...restConfig } = inputFields.labelConfig;
    result['#web-app .labelConfig'] = generateCSS({
      ...restConfig,
      display: 'flex',
    });
  }

  // Form gap
  result['#web-app .finxForm'] = generateCSS({
    display: 'flex',
    'flex-direction': 'column',
    gap: inputFields.finxForm.gap,
  });

  // OTP input field space between
  if (inputFields.otpInputFieldSpacing) {
    result['#web-app .otp-input-field .ng-otp-input-wrapper .otp-input:not(:last-child)'] =
      generateCSSWithImportant(inputFields.otpInputFieldSpacing);
  }

  return result;
}

export function generateCssVarsFromGlobalTheme(globalTheme: GlobalThemeConfiguration): string {
  let css = `:root {\n`;

  // Generate CSS variables for general configurations
  const generalConfig = globalTheme?.generalConfigurations;
  const inputFields = globalTheme?.inputFields;
  const buttons = globalTheme?.buttons;
  const tooltipConfig = globalTheme?.tooltipConfigurations;
  const confirmationDialogConfig = globalTheme?.confirmationDialogConfigurations;
  const typography = globalTheme?.typography;
  const radius = globalTheme?.radius;
  const textSkeleton = globalTheme?.textSkeleton;

  const { defaultSystemFont, fontFamily, palette, secondaryFontFamily } = generalConfig;
  if (generalConfig) {
    css += `
        /* Default/fallback font stack */
        --font-family: ${fontFamily}, system-ui, -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;\n`;
    if (secondaryFontFamily) {
      css += `
        --secondary-font-family: ${secondaryFontFamily}, system-ui, -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;\n`;
    }
    css = generatePaletteVariables(palette, css);
    if (palette.customColors?.length) {
      palette.customColors.forEach((customColor) => {
        css += `  --color-custom-${slugifyPaletteToken(customColor.name)}: ${customColor.code};\n`;
      });
    }
  }
  css += `}\n\n`;
  // if defaultSystemFont then prefer system font on iOS specifically
  if (defaultSystemFont) {
    css += `
      /* Non-iOS devices: Prefer Ginka */
      @supports not (-webkit-touch-callout: none) {
        :root {
          --font-family: "${fontFamily}", system-ui, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
      }

      /* iOS devices: Prefer system-ui */
      @supports (-webkit-touch-callout: none) {
        :root {
          --font-family: system-ui, -apple-system, BlinkMacSystemFont,
            "${fontFamily}", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
      }\n`;
  }
  css += `:root {\n`;

  css += `  --finxone-slider-track-height: 4px;\n`;
  css += `  --finxone-slider-thumb-size: 16px;\n`;
  css += `  --finxone-slider-track-color: var(--color-primary);\n`;
  css += `  --finxone-slider-thumb-color: var(--color-on-primary);\n`;
  css += `  --finxone-slider-bg-color: var(--color-border-color);\n`;
  // Legacy numbered vars - DEPRECATED, kept as aliases for backwards compatibility
  css += `  --palette-color-3: var(--color-primary);\n`;
  css += `  --border-color-0: var(--color-primary);\n`;
  css += `  --palette-color-6: var(--color-background);\n`;
  css += `  --palette-color-5: var(--color-surface);\n`;
  css += `  --palette-color-4: var(--color-border-color);\n`;
  css += `  --border-color-1: var(--color-border-color);\n`;
  css += `  --alerts-color-2: var(--color-info);\n`;
  css += `  --alerts-color-1: var(--color-success);\n`;
  css += `  --alerts-color-3: var(--color-warning);\n`;
  css += `  --alerts-color-0: var(--color-error);\n`;
  css += `  --typography-color-0: var(--color-on-background);\n`;
  css += `  --palette-color-0: var(--color-on-background);\n`;
  css += `  --typography-color-1: var(--color-on-background-muted);\n`;
  css += `  --palette-color-1: var(--color-on-background-muted);\n`;
  css += `  --typography-color-2: var(--color-on-surface-muted);\n`;
  css += `  --border-color-2: var(--color-on-surface-muted);\n`;
  // Legacy color vars without groups, kept as aliases for backwards compatibility
  css += `  --color-primary: var(--color-main-primary);\n`;
  css += `  --color-background: var(--color-main-background);\n`;
  css += `  --color-surface: var(--color-main-surface);\n`;
  css += `  --color-border-color: var(--color-border-default);\n`;
  css += `  --color-active: var(--color-states-active);\n`;
  css += `  --color-hover: var(--color-states-hover);\n`;
  css += `  --color-error: var(--color-status-error);\n`;
  css += `  --color-success: var(--color-status-success);\n`;
  css += `  --color-info: var(--color-status-info);\n`;
  css += `  --color-warning: var(--color-status-warning);\n`;
  css += `  --color-on-primary: var(--color-typography-on-primary);\n`;
  css += `  --color-on-primary-muted: var(--color-typography-on-primary-muted);\n`;
  css += `  --color-on-background: var(--color-typography-on-background);\n`;
  css += `  --color-on-background-muted: var(--color-typography-on-background-muted);\n`;
  css += `  --color-on-surface: var(--color-typography-on-surface);\n`;
  css += `  --color-on-surface-muted: var(--color-typography-on-surface-muted);\n`;
  if (inputFields) {
    css += `--inputFieldHeight: ${inputFields.inputStyles.height};\n`;
    css += `--inputOtpGap: ${inputFields.otpInputFieldSpacing['margin-right']};\n`;
    css += `--inputFieldLabelType: ${inputFields.labelConfig.type};\n`;
    css += `--inputFieldTextStylingClass: ${inputFields.textStyleClass};\n`;
    css += `--inputFieldBorderRadiusClass: ${getBorderRadiusClass(inputFields.borderRadiusClass)}; \n`;
    // Input styles in vars
    css += `--inputFieldIdleBackgroundColor: ${inputFields.idle['background-color']};\n`;
    css += `--inputFieldIdleTextColor: ${inputFields.idle.textColor.color};\n`;
    css += `--inputFieldIdleBorderColor: ${inputFields.idle['border-color']};\n`;
    css += `--inputFieldActiveBackgroundColor: ${inputFields.active['background-color']};\n`;
    css += `--inputFieldActiveTextColor: ${inputFields.active.textColor.color};\n`;
    css += `--inputFieldActiveBorderColor: ${inputFields.active['border-color']};\n`;
    css += `--inputFieldBorderTopWidth: ${inputFields.inputStyles['border-top-width']};\n`;
    css += `--inputFieldBorderBottomWidth: ${inputFields.inputStyles['border-bottom-width']};\n`;
    css += `--inputFieldBorderLeftWidth: ${inputFields.inputStyles['border-left-width']};\n`;
    css += `--inputFieldBorderRightWidth: ${inputFields.inputStyles['border-right-width']};\n`;
    css += `--inputFieldBorderTopLeftRadius: ${inputFields.inputStyles['border-top-left-radius']};\n`;
    css += `--inputFieldBorderTopRightRadius: ${inputFields.inputStyles['border-top-right-radius']};\n`;
    css += `--inputFieldBorderBottomRightRadius: ${inputFields.inputStyles['border-bottom-right-radius']};\n`;
    css += `--inputFieldBorderBottomLeftRadius: ${inputFields.inputStyles['border-bottom-left-radius']};\n`;
    css += `--inputFieldFinxFormGap: ${inputFields.finxForm.gap};\n`;
    css += `--inputFieldLabelColor: ${inputFields.idle.labelColor.color};\n`;
  }
  if (buttons) {
    const { primaryButton, menuButton, secondaryButton, tertiaryButton } = buttons;
    if (primaryButton) {
      css += `--primaryButtonBorderRadiusClass: ${getBorderRadiusClass(primaryButton.borderRadiusClass)}; \n`;
    }
    if (secondaryButton) {
      css += `--secondaryButtonBorderRadiusClass: ${getBorderRadiusClass(
        secondaryButton.borderRadiusClass,
      )}; \n`;
    }
    if (tertiaryButton) {
      css += `--tertiaryButtonBorderRadiusClass: ${getBorderRadiusClass(
        tertiaryButton.borderRadiusClass,
      )}; \n`;
    }
    if (menuButton) {
      css += `--menuButtonIcon: ${menuButton.iconConfigurations?.iconName}; \n`;
      css += `--menuButtonBorderRadiusClass: ${getBorderRadiusClass(menuButton.borderRadiusClass)}; \n`;
    }
    css = generateButtonStyleVariables(buttons, css);
  }
  if (radius) {
    css = generateRadiusVariables(radius, css);
  }
  if (textSkeleton) {
    css = generateSkeletonVariables(textSkeleton, css);
  }
  if (tooltipConfig) {
    css += `--tooltipTextStyleClass: ${tooltipConfig.textStyleClass}; \n`;
    css += `--tooltipBorderRadiusClass: ${tooltipConfig.borderRadiusClass}; \n`;
    css += `--tooltipBackgroundColor: ${tooltipConfig['background-color']}; \n`;
    css += `--tooltipBorderColor: ${tooltipConfig['border-color']}; \n`;
    css += `--tooltipBoxShadow: ${tooltipConfig['box-shadow']}; \n`;
    css += `--tooltipBorderWidth: ${
      tooltipConfig['border-top-width'] +
      ' ' +
      tooltipConfig['border-right-width'] +
      ' ' +
      tooltipConfig['border-bottom-width'] +
      ' ' +
      tooltipConfig['border-left-width']
    }; \n`;
    css += `--tooltipBorderStyle: ${tooltipConfig['border-style']}; \n`;
  }
  if (confirmationDialogConfig) {
    css += `--confirmDialogPositiveButtonClass: ${confirmationDialogConfig.positiveButtonClass}; \n`;
    css += `--confirmDialogNegativeButtonClass: ${confirmationDialogConfig.negativeButtonClass}; \n`;
  }
  if (typography) {
    css += `--labelFontSize: ${typography.labelText['font-size']}; \n`;
    css = generateTypographyScaleVariables(typography, css);
  }
  css += `}\n\n`;
  return css;
}

/**
 * The type scale as vars, so anything rendering outside `#web-app` — a custom
 * widget in its own sandboxed document, most of all — can match the platform's
 * text without re-deriving it. The class rules (`mapTypographyStylesToCssString`)
 * stay: they are what the platform's own markup uses.
 */
const TYPOGRAPHY_VAR_PROPERTIES: (keyof TypographyStyleConfigurations)[] = [
  'font-size',
  'font-weight',
  'line-height',
  'letter-spacing',
];

function generateTypographyScaleVariables(
  typography: GlobalThemeConfiguration['typography'],
  css: string,
): string {
  for (const key of Object.values(TypographyKeys)) {
    const styles = typography[key];
    if (!styles) continue;
    for (const property of TYPOGRAPHY_VAR_PROPERTIES) {
      const value = styles[property];
      if (value) css += `  --typography-${key}-${property}: ${value};\n`;
    }
  }
  return css;
}

/**
 * Corner order is the `border-radius` shorthand order, so the collapsed
 * `--radius-rad-N` var can be dropped straight into a `border-radius`.
 */
const RADIUS_CORNERS: [string, keyof BorderRadiusStyles][] = [
  ['top-left', 'border-top-left-radius'],
  ['top-right', 'border-top-right-radius'],
  ['bottom-right', 'border-bottom-right-radius'],
  ['bottom-left', 'border-bottom-left-radius'],
];

function generateRadiusVariables(radius: GlobalThemeConfiguration['radius'], css: string): string {
  const steps: [BorderRadiusKeys, string][] = [
    [BorderRadiusKeys.RADIUS_ONE, 'rad-1'],
    [BorderRadiusKeys.RADIUS_TWO, 'rad-2'],
  ];
  for (const [key, className] of steps) {
    const corners = radius[key];
    if (!corners) continue;
    const values = RADIUS_CORNERS.map(([, property]) => corners[property]);
    RADIUS_CORNERS.forEach(([corner], index) => {
      if (values[index]) css += `  --radius-${className}-${corner}: ${values[index]};\n`;
    });
    // Only when every corner is configured, otherwise the shorthand would silently
    // reset the corners the theme left alone.
    if (values.every((value) => !!value)) {
      const uniform = new Set(values).size === 1;
      css += `  --radius-${className}: ${uniform ? values[0] : values.join(' ')};\n`;
    }
  }
  return css;
}

function generateSkeletonVariables(
  textSkeleton: GlobalThemeConfiguration['textSkeleton'],
  css: string,
): string {
  const candidates = [textSkeleton[TypographyKeys.BODY1], ...Object.values(textSkeleton)];

  const backgroundColor = candidates.find((styles) => styles?.['background-color'])?.['background-color'];
  if (backgroundColor) {
    css += `  --skeleton-background-color: ${backgroundColor};\n`;
  }

  const corners = candidates.find((styles) => RADIUS_CORNERS.every(([, property]) => styles?.[property]));
  if (corners) {
    const values = RADIUS_CORNERS.map(([, property]) => corners[property]);
    const uniform = new Set(values).size === 1;
    css += `  --skeleton-border-radius: ${uniform ? values[0] : values.join(' ')};\n`;
  }

  return css;
}

/** `border-top-width` -> `BorderTopWidth`, for the file's camelCase var names. */
function toVarNameSuffix(property: string): string {
  return property
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

const BUTTON_BASE_VAR_PROPERTIES: (keyof ButtonConfigBaseStyles)[] = [
  'border-style',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-right-radius',
  'border-bottom-left-radius',
];

/**
 * `extractRootVariables` (ConfigService/ThemeUtilityService) reads generated theme CSS back out
 * via a naive, non-brace-aware regex (`:root\s*{([^}]*)}`) that stops at the FIRST `}` it finds -
 * it does not understand balanced blocks. A loading-text value containing handlebars syntax (e.g.
 * `{{nominatedAccountStatus}}`) has an unbalanced-looking `}` from that regex's point of view,
 * which truncates the captured :root content right there, silently dropping this declaration and
 * everything after it. Since real user-authored loading text can contain arbitrary handlebars
 * (`{{...}}`, block helpers, etc.), literal `{`/`}` must never reach that regex - encode them out
 * before writing the CSS var (below) and decode them back in `resolveButtonLoadingConfigFromCss`,
 * the one place that reads the value before handing it to Handlebars.
 */
const HANDLEBARS_BRACE_PLACEHOLDERS: Record<string, string> = {
  '{': '__FX_HB_OPEN__',
  '}': '__FX_HB_CLOSE__',
};

function encodeCssSafeLoadingText(loadingText: string): string {
  return loadingText.replace(/[{}]/g, (brace) => HANDLEBARS_BRACE_PLACEHOLDERS[brace]);
}

function decodeCssSafeLoadingText(loadingText: string): string {
  return loadingText.replace(/__FX_HB_OPEN__/g, '{').replace(/__FX_HB_CLOSE__/g, '}');
}

/**
 * Per-button-type colours and borders as vars — `--primaryButtonIdleBackgroundColor`
 * and friends. Padding and height are deliberately left out: they are per *size*
 * (small/large/full-width), which would be another 60 vars for a shape widgets
 * cannot use without also knowing which size class they are in.
 */
function generateButtonStyleVariables(buttons: GlobalThemeConfiguration['buttons'], css: string): string {
  for (const buttonType of Object.values(ButtonTypes)) {
    const button = buttons[buttonType];
    if (!button) continue;
    for (const state of ['idle', 'hover', 'active'] as const) {
      const colors = button[state];
      if (!colors) continue;
      const stateName = state.charAt(0).toUpperCase() + state.slice(1);
      const values: [string, string | undefined][] = [
        ['BackgroundColor', colors['background-color']],
        ['TextColor', colors.textColor?.color],
        ['BorderColor', colors['border-color']],
      ];
      for (const [suffix, value] of values) {
        if (value) css += `  --${buttonType}${stateName}${suffix}: ${value};\n`;
      }
    }
    if (button.loading) {
      css += `  --${buttonType}LoadingIndicatorType: ${button.loading.indicatorType};\n`;
      if (button.loading.loadingText) {
        css += `  --${buttonType}LoadingText: ${encodeCssSafeLoadingText(button.loading.loadingText)};\n`;
      }
    }
    // Tertiary buttons carry no baseStyles of their own (they inherit primary's).
    const baseStyles = button.baseStyles;
    if (!baseStyles) continue;
    for (const property of BUTTON_BASE_VAR_PROPERTIES) {
      const value = baseStyles[property];
      if (value) css += `  --${buttonType}${toVarNameSuffix(property)}: ${value};\n`;
    }
  }
  return css;
}

/**
 * Resolves the raw (uncompiled - may contain handlebars placeholders) loading indicator type and
 * text for a button from its rendered CSS class and the currently-active global CSS variables
 * (as emitted by `generateButtonStyleVariables` above). Shared by every consumer that needs to
 * read the globally-configured loading style (the runtime cta-button-widget and the Global
 * Styling preview) so the button-type-matching logic lives in exactly one place.
 */
export function resolveButtonLoadingConfigFromCss(
  cssVars: Record<string, string>,
  buttonClass: string,
): { indicatorType: ButtonLoadingIndicatorType; rawLoadingText: string } {
  const buttonTypePattern = new RegExp(Object.values(ButtonTypes).join('|'), 'i');
  const matchedButtonType = buttonClass.split(' ').find((classItem) => buttonTypePattern.test(classItem));
  if (!matchedButtonType) {
    return { indicatorType: ButtonLoadingIndicatorType.ANIMATION, rawLoadingText: '' };
  }
  return {
    indicatorType:
      (cssVars[`--${matchedButtonType}LoadingIndicatorType`] as ButtonLoadingIndicatorType) ||
      ButtonLoadingIndicatorType.ANIMATION,
    rawLoadingText: decodeCssSafeLoadingText(cssVars[`--${matchedButtonType}LoadingText`] ?? ''),
  };
}

// Helper function to generate CSS string from an object
export const generateCSS = (styleObj: any): string => {
  return Object.entries(styleObj)
    .map(([key, value]) => `${key}: ${value};`)
    .join(' ');
};
export function mapBorderRadiusStylesToCssString(radius: GlobalThemeConfiguration['radius']): MappedStyles {
  const result: MappedStyles = {};
  result['#web-app .rad-1'] = generateCSS({
    'border-top-left-radius': radius[BorderRadiusKeys.RADIUS_ONE]['border-top-left-radius'],
    'border-top-right-radius': radius[BorderRadiusKeys.RADIUS_ONE]['border-top-right-radius'],
    'border-bottom-right-radius': radius[BorderRadiusKeys.RADIUS_ONE]['border-bottom-right-radius'],
    'border-bottom-left-radius': radius[BorderRadiusKeys.RADIUS_ONE]['border-bottom-left-radius'],
  });
  result['#web-app .rad-2'] = generateCSS({
    'border-top-left-radius': radius[BorderRadiusKeys.RADIUS_TWO]['border-top-left-radius'],
    'border-top-right-radius': radius[BorderRadiusKeys.RADIUS_TWO]['border-top-right-radius'],
    'border-bottom-right-radius': radius[BorderRadiusKeys.RADIUS_TWO]['border-bottom-right-radius'],
    'border-bottom-left-radius': radius[BorderRadiusKeys.RADIUS_TWO]['border-bottom-left-radius'],
  });
  return result;
}

export const generateCSSWithImportant = (styleObj: any): string => {
  return Object.entries(styleObj)
    .map(([key, value]) => `${key}: ${value} !important;`)
    .join(' ');
};

function formatCssVariableName(paramName: string): string {
  return paramName
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert a dash between lowercase and uppercase letters
    .toLowerCase(); // Convert the entire string to lowercase
}

function createButtonSizeClassesStyles(
  button: ButtonConfigurations | Pick<ButtonConfigurations, 'idle' | 'active' | 'hover'>,
  result: { [key: string]: string },
  baseSelector: string,
): MappedStyles {
  Object.keys((button as ButtonConfigurations).size).forEach((size) => {
    const { paddingWithIcon, heightConfig, ...restStyles } = (button as ButtonConfigurations).size[
      size as ButtonSizeOptions
    ];
    const extraStyles = setButtonHeight(heightConfig);
    const buttonStyles = { ...(extraStyles ? { ...extraStyles } : {}), ...restStyles };
    result[`#web-app ${baseSelector}.${size}`] = generateCSS(buttonStyles);
    if ((button as ButtonConfigurations).size[size as ButtonSizeOptions]?.paddingWithIcon) {
      result[`#web-app ${baseSelector}.${size}.with-icon`] = generateCSSWithImportant(paddingWithIcon);
    }
  });
  return result;
}

function setButtonHeight(heightConfig?: ButtonHeightConfig) {
  if (heightConfig?.heightType === ButtonHeightSizeOptions.FIXED) {
    return { height: `${heightConfig.height}${heightConfig.heightUnit}` };
  }
  return {};
}
function getTextAlignmentValue(justifyContentValue: string): string {
  switch (justifyContentValue) {
    case 'start':
      return 'left';
    case 'center':
      return 'center';
    case 'end':
      return 'right';
    default:
      return 'left';
  }
}

function getBorderRadiusClass(borderRadiusClass: string): string {
  switch (borderRadiusClass) {
    case BorderRadiusKeys.RADIUS_ONE:
      return 'rad-1';
    case BorderRadiusKeys.RADIUS_TWO:
      return 'rad-2';
    default:
      return '';
  }
}

function mapTooltipStylesToCssString(tooltipConfigurations: TooltipStyleConfigurations) {
  const result: MappedStyles = {};
  // destructure the config object to extract css properties
  const { textStyleClass, borderRadiusClass, ...valuesToUse } = tooltipConfigurations;
  result[`#web-app .finx-tooltip`] = generateCSS({
    ...valuesToUse,
    'text-align': valuesToUse['justify-content'],
  });
  return result;
}

function generatePaletteVariables(palette: NewColourPaletteType, css: string) {
  for (const [groupName, group] of Object.entries(palette)) {
    if (groupName === 'customColors') continue; // handled separately, legacy shape
    for (const [key, value] of Object.entries(group)) {
      css += `  ${getPaletteCssVariableName(groupName, key)}: ${value};\n`;
    }
  }
  return css;
}

/**
 * Slugifies a palette group or colour name into consistent kebab-case for use
 * in a CSS custom-property name (e.g. "Raspberry" -> "raspberry", "Sea Foam"
 * -> "sea-foam", "onPrimaryMuted" -> "on-primary-muted"). Display names are
 * never stored slugified — the palette object's own key doubles as the
 * display name, and slugifying only happens here at CSS-generation time.
 */
export function slugifyPaletteToken(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase();
}

/**
 * The single source of truth for a palette colour's generated CSS variable
 * name. Both the group name and the colour name are slugified into kebab-case,
 * for fixed groups (`main`/`border`/`states`/`status`/`typography`) and
 * custom (user-defined) groups alike. Shared by the CSS generator and by any
 * UI previewing a colour's variable name, so the two can never drift.
 */
export function getPaletteCssVariableName(groupName: string, colourName: string): string {
  return `--color-${slugifyPaletteToken(groupName)}-${slugifyPaletteToken(colourName)}`;
}

export function ButtonSizeStyleDefaults() {
  return {
    'padding-top': '',
    'padding-right': '',
    'padding-bottom': '',
    'padding-left': '',
    'margin-top': '',
    'margin-right': '',
    'margin-bottom': '',
    'margin-left': '',
    heightConfig: {
      heightType: ButtonHeightSizeOptions.HUG,
      height: '55',
      heightUnit: 'px',
    },
  };
}

export function paddingWithIconStyleDefaults() {
  return {
    gap: '',
    'padding-right': '',
    'padding-left': '',
  };
}

export function buttonSizeDefaults() {
  return {
    'padding-top': '',
    'padding-right': '',
    'padding-bottom': '',
    'padding-left': '',
    'margin-top': '',
    'margin-right': '',
    'margin-bottom': '',
    'margin-left': '',
  };
}

export function buttonColorDefaults(): Omit<ButtonAndInputStatesConfig, 'labelColor'> {
  return {
    textColor: { color: '' },
    'border-color': '',
    'background-color': '',
  };
}

export function inputColorDefaults(): ButtonAndInputStatesConfig {
  return {
    textColor: { color: '' },
    'border-color': '',
    'background-color': '',
    labelColor: { color: '' },
  };
}

export function buttonBaseStyles() {
  return {
    'justify-content': '',
    'align-items': '',
    'border-style': '',
    'box-shadow': '',
    'border-top-left-radius': '',
    'border-top-right-radius': '',
    'border-bottom-right-radius': '',
    'border-bottom-left-radius': '',
    'border-top-width': '',
    'border-left-width': '',
    'border-bottom-width': '',
    'border-right-width': '',
  };
}

export function inputBaseDefaults(): InputBaseStyles {
  return {
    'justify-content': '',
    'align-items': '',
    'border-style': '',
    'box-shadow': '',
    'border-top-left-radius': '',
    'border-top-right-radius': '',
    'border-bottom-right-radius': '',
    'border-bottom-left-radius': '',
    'border-top-width': '',
    'border-left-width': '',
    'border-bottom-width': '',
    'border-right-width': '',
    'padding-top': '',
    'padding-right': '',
    'padding-bottom': '',
    'padding-left': '',
    height: '',
    width: '',
  };
}

export function tooltipStyleDefaults(): TooltipStyleConfigurations {
  return {
    'justify-content': '',
    'align-items': '',
    'border-style': '',
    'box-shadow': '',
    'border-top-left-radius': '',
    'border-top-right-radius': '',
    'border-bottom-right-radius': '',
    'border-bottom-left-radius': '',
    'border-top-width': '',
    'border-left-width': '',
    'border-bottom-width': '',
    'border-right-width': '',
    'padding-top': '',
    'padding-right': '',
    'padding-bottom': '',
    'padding-left': '',
    'background-color': '',
    'border-color': '',
    color: '',
    borderRadiusClass: BorderRadiusKeys.NONE,
    textStyleClass: '',
  };
}

export function inputLabelDefaults(): InputLabelConfig {
  return {
    type: InputLabelOptions.STATIC,
    'align-items': '',
    'justify-content': '',
    'padding-top': '',
    'padding-right': '',
    'padding-bottom': '',
    'padding-left': '',
  };
}

export function buttonIconConfigurationDefaults(): ButtonIconConfigurations {
  return {
    type: 'right',
    iconName: '',
    height: '',
    width: '',
    color: '',
  };
}
